/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SimpleRoulette from "./SimpleRoulette";
import { PRIZES } from "../../utils/prizes";

const LOADING_PHRASES = [
  "Synchronizing neural pathways...",
  "Decrypting dream-fragments from the void...",
  "Bootstrapping simulation cortex...",
  "Re-indexing soul shards in the data lattice...",
  "Aligning quantum entropy vectors...",
  "Splicing loot-seeds into neural fabric...",
  "Calibrating photon synapses...",
  "Fetching chaos hash from mainframe...",
  "Establishing uplink with Harkan Nexus...",
  "Reanimating dormant protocol ghosts...",
  "Spawning loot probability daemon...",
  "Cross-compiling cosmic RNG sequence...",
  "Patching memory leak in the metaverse...",
  "Reconstructing lost bytes of euphoria...",
  "Negotiating access with rogue AI instance...",
];

export type SimpleRouletteItem = {
  id: number;
  name: string;
  image: string;
  reward?: string;
  accent?: string;
};

export default function LootboxOpening({
  openingResult,
  boxType = "gold",
  onEnd,
  videoSrc,
}: {
  openingResult: any;
  boxType?: string;
  onEnd?: () => void;
  videoSrc: string;
}) {
  const [step, setStep] = useState<
    "loading" | "video" | "transition" | "roulette"
  >("loading");
  const [winner, setWinner] = useState<SimpleRouletteItem | null>(null);
  const [phrase, setPhrase] = useState(
    LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)]
  );

  const prizesAvailable = PRIZES.filter((p) => p.boxes.includes(boxType));

  const bgImage = `/assets/boxes/LOOTBOX_BG_${boxType.toUpperCase()}.png`;
  // create random prizes for demo purposes using PRIZES as base
  const prizes = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    name: `Prize ${i + 1}`,
    image: prizesAvailable[i % prizesAvailable.length].image,
    accent: ["#f1c40f", "#7fb2ff", "#b36bff", "#ff4b6e"][i % 4],
  }));

  // 🚀 Phase 1: initial fade-in while waiting for tx
  useEffect(() => {
    const t = setTimeout(() => setStep("video"), 1800); // ~1.8s wait
    return () => clearTimeout(t);
  }, []);

  // 🧠 Watch for result from backend
  useEffect(() => {
    if (!openingResult || !openingResult.prizes) return;

    const { type, amount } = openingResult.prizes[0];
    // const foundPrize = prizes[winnerIndex % prizes.length];
    const foundPrize = prizesAvailable.find((p) => p.type === type);

    if (!foundPrize) {
      console.warn(
        "[Lootbox] ❌ Prize from result not found in available prizes:",
        type
      );
      return;
    }

    console.log("[Lootbox] 🎯 Winner received:", foundPrize);

    const isInDollars = type === "gamblor_bonus";

    setWinner({
      ...foundPrize,
      reward: `${isInDollars ? "$" : ""}${amount} `,
    });

    // if (!openingResult || !openingResult.winnerNumbers) return;

    // const winnerIndex = Number(openingResult.winnerNumbers[0]);
    // const foundPrize = prizes[winnerIndex % prizes.length];
    // console.log("[Lootbox] 🎯 Winner received:", winnerIndex, foundPrize);

    // setTimeout(() => setWinner(foundPrize), 800);
  }, [openingResult]);

  // 🎬 Handle video end — soft transition to roulette
  const handleVideoEnd = () => {
    setStep("transition");
    // short burst transition before roulette
    setTimeout(() => setStep("roulette"), 1800);
  };

  // ⏱ change phrase every few seconds while loading or waiting
  useEffect(() => {
    if (step !== "loading" && (step !== "roulette" || winner)) return;

    const interval = setInterval(() => {
      setPhrase(
        LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)]
      );
    }, 1700); // every 1.7 seconds

    return () => clearInterval(interval);
  }, [step, winner]);

  useEffect(() => {
    if (step === "loading") setWinner(null);
  }, [step]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black overflow-hidden lootbox-opening">
      <AnimatePresence mode="wait">
        {/* ⚪ Phase 1: Loading transition */}
        {step === "loading" && (
          <AnimatePresence mode="wait">
            <motion.p
              key={phrase} // 👈 triggers exit/enter animation when phrase changes
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="text-lg font-light tracking-widest uppercase opacity-80 text-center max-w-xs"
            >
              {phrase}
            </motion.p>
          </AnimatePresence>
        )}

        {/* 🎥 Phase 2: Opening video */}
        {step === "video" && (
          <motion.video
            key={boxType}
            src={videoSrc}
            autoPlay
            playsInline
            muted
            onEnded={handleVideoEnd}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="w-full h-full object-cover relative z-10"
          />
        )}

        {/* ⚡ Phase 3: simple transition */}
        {step === "transition" && (
          <motion.div
            key="transition"
            className="absolute inset-0  z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
          />
        )}

        {step === "roulette" && !winner && (
          <AnimatePresence mode="wait">
            <motion.p
              key={phrase} // 👈 triggers exit/enter animation when phrase changes
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="text-lg font-light tracking-widest uppercase opacity-80 text-center max-w-xs"
            >
              {phrase}
            </motion.p>
          </AnimatePresence>
        )}

        {/* 🎡 Phase 4: Roulette */}
        {step === "roulette" && winner && (
          <motion.div
            key="roulette"
            className="absolute inset-0 flex items-center justify-center z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <img
              src={bgImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none"
              style={{
                filter: "brightness(1.1) contrast(1.2) saturate(1.1)",
              }}
            />
            <SimpleRoulette
              items={prizes}
              winner={winner}
              spinMs={8500}
              onClose={onEnd}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
